import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import type { ToolSummary } from '../../api/TraceabilityResponseType';
import { findNearTool, findTool, nextFreeToolId, similarToolIds } from '../../utils/toolSummaries';
import { Highlight } from '@/components/form/inputs/combobox/ComboboxPopup';
import { PickerStatus } from './PickerStatus';
import { countText, toolReferenceEditPath } from './pickerHelpers';

/** Similar IDs listed at most; "et N autres" counts the rest. */
export const EXISTING_IDS_MAX_ROWS = 6;

export type ExistingToolIdsProps = {
  id?: string;
  value: string;
  /** The company's tools; null → nothing is shown (the form behaves as before). */
  summaries: readonly ToolSummary[] | null;
  /** "Utiliser" on the next free number. */
  onUse?: (toolId: string) => void;
  /** False for a fixed (scanned) ID: only the refusal is shown, no list. */
  showSimilar?: boolean;
};

/**
 * Under the ID field of the tool creation forms: refuses an ID that already exists (with a link
 * to change that tool's reference instead), warns about a case-only difference, and lists the
 * similar IDs already registered so the new one can be named after them. The rows are not
 * selectable: an existing ID cannot be created twice.
 */
export function ExistingToolIds({
  id,
  value,
  summaries,
  onUse,
  showSimilar = true,
}: ExistingToolIdsProps) {
  const { t } = useTranslation();
  if (!summaries) return null;

  const typed = value.trim();
  if (!typed) {
    return showSimilar ? (
      <PickerStatus kind="hint" id={id} testId="existing-ids-hint">
        {t('toolPickers.existingIds.hint')}
      </PickerStatus>
    ) : null;
  }

  const taken = findTool(typed, summaries);
  const near = taken ? null : findNearTool(typed, summaries);
  const similar = showSimilar ? similarToolIds(typed, summaries) : [];
  const next = similar.length > 0 ? nextFreeToolId(typed, summaries) : null;
  const nextIsFree = next !== null && !findTool(next, summaries);

  return (
    <div id={id} className="flex flex-col gap-2">
      {taken && (
        <PickerStatus kind="error" testId="tool-id-taken">
          <p>
            <strong>{t('toolPickers.existingIds.takenTitle')}</strong>{' '}
            {t('toolPickers.existingIds.taken', {
              reference: taken.reference ?? t('toolPickers.toolId.noReference'),
              traces: countText(t, 'toolPickers.toolId.traces', taken.traceCount, true),
            })}
          </p>
          <Link
            to={toolReferenceEditPath(taken.toolId)}
            className="mt-1 inline-block font-medium text-primary underline-offset-2 hover:underline"
          >
            {t('toolPickers.existingIds.openTool', { toolId: taken.toolId })}
          </Link>
        </PickerStatus>
      )}
      {near && (
        <PickerStatus kind="near" testId="tool-id-near">
          {t('toolPickers.existingIds.near', { toolId: near.toolId })}
        </PickerStatus>
      )}
      {similar.length > 0 && (
        <div
          role="note"
          data-testid="existing-ids"
          className="flex flex-col gap-1.5 rounded-lg border border-border bg-muted/60 px-3 py-2"
        >
          <span className="eyebrow">
            {t('toolPickers.existingIds.listTitle', { count: similar.length })}
          </span>
          <ul className="flex flex-col gap-0.5">
            {similar.slice(0, EXISTING_IDS_MAX_ROWS).map((tool) => (
              <li
                key={tool.toolId}
                className="flex justify-between gap-3 text-xs text-muted-foreground"
              >
                <span className="truncate font-mono text-foreground/75">
                  <Highlight text={tool.toolId} query={typed} />
                </span>
                <span className="shrink-0">{tool.reference ?? '—'}</span>
              </li>
            ))}
          </ul>
          {similar.length > EXISTING_IDS_MAX_ROWS && (
            <span className="text-xs text-muted-foreground">
              {t('toolPickers.existingIds.more', {
                count: similar.length - EXISTING_IDS_MAX_ROWS,
              })}
            </span>
          )}
          {nextIsFree && (
            <div className="flex flex-wrap items-center gap-1 border-t border-border pt-1.5 text-xs">
              <span>{t('toolPickers.existingIds.nextFree')}</span>
              <code className="font-mono">{next}</code>
              {onUse && (
                <Button
                  type="button"
                  variant="link"
                  size="inline"
                  className="text-xs"
                  onClick={() => onUse(next)}
                >
                  {t('toolPickers.existingIds.use')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
